import {AnalyticsProvider} from '@github-ui/analytics-provider'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {IssueViewer} from '@github-ui/issue-viewer/IssueViewer'
import {IssueViewerContextProvider} from '@github-ui/issue-viewer/IssueViewerContextProvider'
import type {OptionConfig} from '@github-ui/issue-viewer/OptionConfig'
import {TanStackIssueViewer} from '@github-ui/issue-viewer/TanStackIssueViewer'
import type {ItemIdentifier} from '@github-ui/issue-viewer/Types'
import {noop} from '@github-ui/noop'
/* eslint-disable-next-line @github-ui/github-monorepo/no-use-app-payload --
   useAppPayload is deprecated. See
   https://thehub.github.com/epd/engineering/dev-practicals/frontend/react/data-router/migration/app-payload-deprecation/ */
import {AppPayloadContext} from '@github-ui/react-core/use-app-payload'
import type {AppPayloadWithFeatureFlags} from '@github-ui/react-core/use-feature-flag'
import {
  RelayCachePreheatContextProvider,
  TanStackCachePreheatContextProvider,
} from '@github-ui/relay-query-cache/contexts/RelayCachePreheatContext'
import {StaleCacheBanner} from '@github-ui/stale-cache-banner'
import type {SubIssueSidePanelItem as SubIssueItem} from '@github-ui/sub-issues/sub-issue-types'
import {ArchiveIcon, LinkExternalIcon, ProjectSymlinkIcon, TrashIcon} from '@primer/octicons-react'
import {ActionList} from '@primer/react'
import {useQueryClient} from '@tanstack/react-query'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'

import {SubIssueSidePanelItem} from '../../api/memex-items/hierarchy'
import {SidePanelLoadDuration} from '../../api/stats/contracts'
import {SidePanelUI} from '../../api/stats/contracts/common'
import {getEnabledFeatures} from '../../helpers/feature-flags'
import {getInitialState} from '../../helpers/initial-state'
import {getIssueItemIdentifier} from '../../helpers/item-url'
import {fetchJSONIslandData} from '../../helpers/json-island'
import {ViewerPrivileges} from '../../helpers/viewer-privileges'
import {usePostStats} from '../../hooks/common/use-post-stats'
import {useArchiveMemexItemsWithConfirmation} from '../../hooks/use-archive-memex-items-with-confirmation'
import {useRemoveMemexItemWithConfirmation} from '../../hooks/use-remove-memex-items-with-id'
import {useSidePanel} from '../../hooks/use-side-panel'
import {useSidePanelDirtyState} from '../../hooks/use-side-panel-dirty-state'
import {MEMEX_ISSUE_DELETION_CONNECTION} from '../../state-providers/memex-items/queries/issue-deletion'
import {usePaginatedMemexItemsQueryContext} from '../../state-providers/memex-items/queries/paginated-memex-items-query-context'
import {removeMemexItemsFromQueryClient} from '../../state-providers/memex-items/query-client-api/memex-items/remove-items'
import {useMemexItems} from '../../state-providers/memex-items/use-memex-items'
import {IssueViewerResources} from '../../strings'
import {SidePanelToolbar} from './header'
import styles from './shared-issue-viewer.module.css'
import {useIssueDeletionCompletion} from './use-issue-deletion-completion'

type SharedIssueViewerProps = {owner: string; repo: string; number: number; url: string; itemId: number}

export function SharedIssueViewer(props: SharedIssueViewerProps) {
  const {relayIds, projectOwner} = getInitialState()
  const memexFeatureFlags = getEnabledFeatures()
  const {postStats} = usePostStats()
  const loadTrackedRef = useRef(false)

  const appPayload = useMemo(() => fetchJSONIslandData('app-payload') || {}, [])

  const appPayloadWithFeatureFlags: AppPayloadWithFeatureFlags = useMemo(
    () => ({
      ...appPayload,
      enabled_features: memexFeatureFlags,
    }),
    [appPayload, memexFeatureFlags],
  )

  const [currentProps, setCurrentProps] = useState<
    Omit<SharedIssueViewerProps, 'itemId'> & {itemId: number | undefined}
  >(props)

  const {hasWritePermissions} = ViewerPrivileges()
  const {items} = useMemexItems()
  const queryClient = useQueryClient()
  const {openProjectItemInPane, reloadPaneItem} = useSidePanel()
  const paginatedItemsContext = usePaginatedMemexItemsQueryContext()

  const findMemexItemByIdentifier = useCallback(
    (itemIdentifier: ItemIdentifier) => {
      return items.find(item => {
        const comparisonIdentifier = item.getItemIdentifier()
        if (!comparisonIdentifier) return false
        return (
          comparisonIdentifier.owner === itemIdentifier.owner &&
          comparisonIdentifier.repo === itemIdentifier.repo &&
          comparisonIdentifier.number === itemIdentifier.number
        )
      })
    },
    [items],
  )

  useEffect(() => {
    const {owner, repo, number, url, itemId} = props
    const memexItem = findMemexItemByIdentifier({
      owner,
      repo,
      number,
      type: 'Issue',
    })
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setCurrentProps({owner, repo, number, url, itemId: memexItem ? itemId : undefined})
  }, [findMemexItemByIdentifier, props])

  // Handles opening a 'potential' memex item. Items found
  // within the current project will be opened in the sidepanel,
  // while items not found will be opened in a new tab.
  const openMemexItem = useCallback(
    (itemIdentifier: ItemIdentifier | undefined, url: string) => {
      const memexItem = itemIdentifier && findMemexItemByIdentifier(itemIdentifier)

      if (!memexItem || !itemIdentifier) {
        // Link is not to an issue in the project, open in new tab
        window.open(url, '_blank')
        return
      }

      openProjectItemInPane(memexItem)
      setCurrentProps({
        owner: itemIdentifier.owner,
        repo: itemIdentifier.repo,
        number: itemIdentifier.number,
        url,
        itemId: memexItem.id,
      })
    },
    [findMemexItemByIdentifier, openProjectItemInPane],
  )

  const onLinkClick = useCallback(
    (event: MouseEvent) => {
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (event.metaKey || event.shiftKey || event.button === 1 || !event.target) return
      if (!(event.target instanceof Element)) return

      const linkUrl = event.target.closest('a')?.href
      if (!linkUrl) return

      event.preventDefault()

      const itemIdentifier = getIssueItemIdentifier(linkUrl)

      openMemexItem(itemIdentifier, linkUrl)
    },
    [openMemexItem],
  )

  const onSubIssueClick = useCallback(
    (subIssueItem: SubIssueItem) => {
      const {id, owner, repo, number, url} = subIssueItem
      const memexItem = findMemexItemByIdentifier({owner, repo, number, type: 'Issue'})
      const sidePanelItem = new SubIssueSidePanelItem(subIssueItem)
      // If the sub-issue is an item in this project, update the side panel's references
      // Otherwise, only update the issue-viewer's content in place.
      if (memexItem) {
        openProjectItemInPane(memexItem)
      } else {
        openProjectItemInPane(sidePanelItem)
      }

      setCurrentProps({
        owner,
        repo,
        number,
        url,
        itemId: memexItem ? id : undefined,
      })
    },
    [findMemexItemByIdentifier, openProjectItemInPane],
  )

  const {closePane} = useSidePanel()

  const {openArchiveConfirmationDialog} = useArchiveMemexItemsWithConfirmation(undefined, undefined, undefined, () =>
    closePane({force: true}),
  )

  const {openRemoveConfirmationDialog} = useRemoveMemexItemWithConfirmation(undefined, undefined, undefined, () =>
    closePane({force: true}),
  )

  const onOpenInNewTab = useCallback(() => {
    window.open(currentProps.url)
  }, [currentProps.url])

  const onCopyLinkInProject = useCallback(() => {
    navigator.clipboard.writeText(window.location.toString())
  }, [])

  const onArchiveInProject = useCallback(() => {
    if (currentProps.itemId) {
      openArchiveConfirmationDialog([currentProps.itemId], SidePanelUI)
    }
  }, [currentProps.itemId, openArchiveConfirmationDialog])

  const onDeleteFromProject = useCallback(() => {
    if (currentProps.itemId) {
      openRemoveConfirmationDialog([currentProps.itemId], SidePanelUI)
    }
  }, [currentProps.itemId, openRemoveConfirmationDialog])

  const customEditMenuEntries = [
    <ActionList.Item key="open-in-new-tab" onSelect={onOpenInNewTab}>
      <ActionList.LeadingVisual>
        <LinkExternalIcon />
      </ActionList.LeadingVisual>
      {IssueViewerResources.openInNewTab}
    </ActionList.Item>,
  ].concat(
    // Currently, non-memex items shown in the sidepanel cannot be navigated to directly.
    // In those cases, hide the menu option for copying the project link.
    currentProps.itemId
      ? [
          <ActionList.Item key="copy-link-in-project" onSelect={onCopyLinkInProject}>
            <ActionList.LeadingVisual>
              <ProjectSymlinkIcon />
            </ActionList.LeadingVisual>
            {IssueViewerResources.copyLinkInProject}
          </ActionList.Item>,
        ]
      : [],
    currentProps.itemId && hasWritePermissions
      ? [
          <ActionList.Item key="archive-in-project" onSelect={onArchiveInProject}>
            <ActionList.LeadingVisual>
              <ArchiveIcon />
            </ActionList.LeadingVisual>
            {IssueViewerResources.archiveInProject}
          </ActionList.Item>,
          <ActionList.Item variant="danger" key="delete-from-project" onSelect={onDeleteFromProject}>
            <ActionList.LeadingVisual>
              <TrashIcon />
            </ActionList.LeadingVisual>
            {IssueViewerResources.deleteFromProject}
          </ActionList.Item>,
        ]
      : [],
  )

  const onIssueUpdate = useCallback(() => {
    reloadPaneItem()
  }, [reloadPaneItem])

  const onRemovedFromProject = useCallback(() => {
    if (currentProps.itemId) {
      removeMemexItemsFromQueryClient(queryClient, [currentProps.itemId])
    }
  }, [currentProps.itemId, queryClient])

  const {onIssueDelete, navigateAfterIssueDeletion} = useIssueDeletionCompletion({
    owner: currentProps.owner,
    repo: currentProps.repo,
    number: currentProps.number,
    refresh: paginatedItemsContext?.refreshForIssueDeletion,
    closePane,
  })

  const onClose = useCallback(() => {
    closePane({force: true})
  }, [closePane])

  const [activeCommentEdits, setActiveCommentEdits] = useState<Readonly<Record<string, boolean>>>({})
  const [, setCommentsDirty] = useSidePanelDirtyState()
  const [, setBodyDirty] = useSidePanelDirtyState()

  useEffect(() => {
    // Whenever the active comment edit state changes, update the dirty state to sync
    // so that if there is any comment being edited, the state is dirty
    setCommentsDirty(Object.values(activeCommentEdits).some(s => s))
  }, [activeCommentEdits, setCommentsDirty])

  const {owner, repo, number} = currentProps

  // Track side panel load duration when IssueViewer content renders
  const onIssueViewerLoad = useCallback(() => {
    // Only track once per side panel open
    if (loadTrackedRef.current) return
    loadTrackedRef.current = true

    try {
      performance.mark('memex-side-panel-loaded')
      const measure = performance.measure(
        'memex-side-panel-duration',
        'memex-side-panel-opened',
        'memex-side-panel-loaded',
      )
      postStats({
        name: SidePanelLoadDuration,
        context: JSON.stringify({
          duration: measure.duration,
          owner,
          repo,
          number,
        }),
      })
      // Clean up performance marks
      performance.clearMarks('memex-side-panel-opened')
      performance.clearMarks('memex-side-panel-loaded')
      performance.clearMeasures('memex-side-panel-duration')
    } catch {
      // Ignore if performance marks don't exist (e.g., navigated between items quickly)
    }
  }, [postStats, owner, repo, number])

  // Reset load tracking when issue changes
  useEffect(() => {
    loadTrackedRef.current = false
  }, [owner, repo, number])

  const optionConfig: OptionConfig = {
    onLinkClick,
    selectedProjectId: relayIds?.memexProject,
    allowedProjectOwner: projectOwner?.login,
    customEditMenuEntries,
    issueQueriesFetchingPolicy: {
      fetchPolicy: 'store-and-network',
    },
    onIssueUpdate,
    onRemovedFromProject,
    timelineEventBaseUrl: `/${owner}/${repo}/issues`,
    onIssueDelete,
    issueDeletionConnection: paginatedItemsContext
      ? {
          ...MEMEX_ISSUE_DELETION_CONNECTION,
          targets: paginatedItemsContext.issueDeletionTargets,
          getTargets: paginatedItemsContext.getIssueDeletionTargets,
          settleAfterOnCompleted: true,
        }
      : undefined,
    onClose,
    showRepositoryPill: true,
    navigateBack: navigateAfterIssueDeletion,
    navigate: noop,
    additionalHeaderActions: <SidePanelToolbar showCloseButton={false} />,
    onCommentEditStart(commentId) {
      setActiveCommentEdits(previousCommentEditStates => {
        if (previousCommentEditStates[commentId]) return previousCommentEditStates
        return {
          ...previousCommentEditStates,
          [commentId]: true,
        }
      })
    },
    onCommentEditCancel(commentId) {
      setActiveCommentEdits(previousCommentEditStates => {
        if (!previousCommentEditStates[commentId]) return previousCommentEditStates
        return {
          ...previousCommentEditStates,
          [commentId]: false,
        }
      })
    },
    onIssueEditStateChange: setBodyDirty,
    useViewportQueries: false, // We don't want to use viewport queries, because we mount in the side panel
    onSubIssueClick,
    insideSidePanel: true,
    onLoad: onIssueViewerLoad,
  }
  const CachePreheatContextProvider = isFeatureEnabled('issue_viewer_paved_path')
    ? TanStackCachePreheatContextProvider
    : RelayCachePreheatContextProvider

  return (
    <div className={styles.Box}>
      <div className={styles.Box_1}>
        <IssueViewerContextProvider>
          <CachePreheatContextProvider>
            <AnalyticsProvider appName="memex" category="Memex Project" metadata={{}}>
              <StaleCacheBanner />
              <AppPayloadContext value={appPayloadWithFeatureFlags}>
                {isFeatureEnabled('issue_viewer_paved_path') ? (
                  <TanStackIssueViewer owner={owner} repo={repo} number={number} optionConfig={optionConfig} />
                ) : (
                  <IssueViewer
                    itemIdentifier={{
                      owner,
                      repo,
                      number,
                      type: 'Issue',
                    }}
                    optionConfig={optionConfig}
                  />
                )}
              </AppPayloadContext>
            </AnalyticsProvider>
          </CachePreheatContextProvider>
        </IssueViewerContextProvider>
      </div>
    </div>
  )
}
